// Producer project dashboard — top KPIs, character/batch matrix, recent
// take stream, and an "attention required" panel. Subscribes to realtime
// for project + take updates so the matrix and stream stay live.

import { Badge, Button, Card, EmptyState, Spinner, Tooltip } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { useCharacters } from '../../../lib/api/characters'
import { useProducerProject } from '../../../lib/api/producer-projects'
import { producerProjectKey } from '../../../lib/api/producer-projects'
import { useReviewQueue } from '../../../lib/api/producer-takes'
import { reviewQueueKey } from '../../../lib/api/producer-takes'
import type { BatchCell, BatchState } from '../../../lib/api/producer-types'
import { useRequireRole } from '../../../lib/auth'
import { useProducerProjectChannel, useProducerTakesChannel } from '../../../lib/realtime-projects'
import { useRealtimeChannel } from '../../../lib/realtime/use-channel'

export const Route = createFileRoute('/producer/projects/$projectId/')({
  component: ProducerProjectDetail,
})

const STATE_COLOR: Record<BatchState, string> = {
  not_started: 'var(--bg-3)',
  invited: 'var(--bg-3)',
  in_progress: 'var(--amber)',
  qc: 'var(--amber)',
  done: 'var(--green)',
  blocked: 'var(--red)',
}

function ProducerProjectDetail() {
  useRequireRole(['owner', 'member'])
  const { projectId } = Route.useParams()
  const projectQ = useProducerProject(projectId)
  const charactersQ = useCharacters(projectId)
  const reviewQueueQ = useReviewQueue(projectId)

  const orgId = projectQ.data?.organization_id ?? null
  useProducerProjectChannel(orgId)
  useProducerTakesChannel(orgId, projectId)

  // Domain-event subscription on the takes topic — invalidates the project +
  // queue caches so the matrix and recent-takes list reflect new TakeRecorded /
  // TakeApproved / BatchApproved events without a manual refresh.
  useRealtimeChannel({
    orgId,
    topic: 'takes',
    invalidateKeys: [reviewQueueKey(projectId), producerProjectKey(projectId)],
  })
  // Project pipeline events (processing started/completed) refresh the KPI row.
  useRealtimeChannel({
    orgId,
    topic: 'project_progress',
    invalidateKeys: [producerProjectKey(projectId)],
  })

  const project = projectQ.data
  const characters = charactersQ.data ?? []

  const totalLines = project?.total_lines ?? 0
  const approvedLines = project?.approved_lines ?? 0
  const overallPct = totalLines === 0 ? 0 : Math.round((approvedLines / totalLines) * 100)
  const activeActors = project?.active_actors ?? 0
  const totalChars = project?.total_characters ?? 0

  const matrix: BatchCell[][] = useMemo(() => project?.batch_matrix ?? [], [project])

  if (projectQ.isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
          <Spinner /> Loading project…
        </div>
      </Card>
    )
  }
  if (projectQ.isError || !project) {
    return (
      <Card>
        <p className="m-0 text-[13px] text-[var(--red)]">
          Could not load project: {projectQ.error?.message ?? 'unknown error'}
        </p>
      </Card>
    )
  }

  return (
    <section>
      <header className="mb-5 pb-4 border-b border-[var(--border)] flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 text-[12px] text-[var(--text-3)] font-mono uppercase tracking-wide">
            <span>{project.code}</span>
            <Badge variant="blue">{project.status}</Badge>
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] m-0 truncate">
            {project.name}
          </h1>
        </div>
        <ProjectNav projectId={projectId} />
      </header>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Overall progress"
          value={`${overallPct}%`}
          sub={`${approvedLines}/${totalLines} lines`}
        />
        <KpiCard label="Actors activated" value={`${activeActors}/${totalChars}`} />
        <KpiCard label="Outstanding (this month)" value={formatUsd(project.outstanding_usd)} />
        <KpiCard
          label="Project total"
          value={formatUsd(project.total_value_usd)}
          sub={`${formatUsd(project.paid_usd)} paid`}
        />
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-3" data-tour="progress-dashboard">
        <Card title="Character / batch matrix" className="lg:col-span-2">
          {matrix.length === 0 ? (
            <p className="m-0 text-[13px] text-[var(--text-2)]">
              Approve the script book to populate the matrix.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <div
                className="grid gap-1"
                style={{ gridTemplateColumns: `120px repeat(${matrix[0]?.length ?? 0}, 28px)` }}
              >
                <div />
                {(matrix[0] ?? []).map((_, i) => (
                  <div key={i} className="text-center text-[10px] text-[var(--text-3)]">
                    B{i + 1}
                  </div>
                ))}
                {matrix.map((row, ri) => {
                  const character = characters.find((c) => c.id === row[0]?.character_id)
                  return (
                    <CellRow
                      key={ri}
                      label={character?.display_name ?? row[0]?.character_id ?? `?`}
                      cells={row}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </Card>
        <AttentionCard
          pendingTakes={reviewQueueQ.data?.length ?? 0}
          unInvitedCharacters={characters.filter((c) => !c.assigned_actor_id).length}
          projectId={projectId}
        />
      </div>

      <Card title="Recent takes" actions={<RealtimePill />}>
        {reviewQueueQ.isLoading ? (
          <p className="m-0 text-[13px] text-[var(--text-2)]">Loading takes…</p>
        ) : (reviewQueueQ.data ?? []).length === 0 ? (
          <EmptyState
            title="No takes pending review"
            description="Approved takes drop here as actors record."
          />
        ) : (
          <ul className="m-0 list-none space-y-1 p-0">
            {(reviewQueueQ.data ?? []).slice(0, 10).map((take) => (
              <li
                key={take.id}
                className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] px-2 py-1 hover:bg-[var(--bg-3)]"
              >
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="font-mono text-[12px] text-[var(--text-2)]">
                    {take.character_code}
                  </span>
                  <span className="font-mono text-[11px] text-[var(--text-3)]">
                    #{take.cue_number}
                  </span>
                  <span className="truncate text-[13px] text-[var(--text)]">
                    {take.line_text ?? <em className="text-[var(--text-3)]">no text</em>}
                  </span>
                </div>
                <span className="text-[11px] text-[var(--text-3)]">
                  {formatRelative(take.recorded_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  )
}

function ProjectNav({ projectId }: { projectId: string }) {
  return (
    <nav aria-label="Project sections" className="flex flex-wrap items-center gap-1 text-[13px]">
      <Link
        to="/producer/projects/$projectId/setup"
        params={{ projectId }}
        className="no-underline"
      >
        <Button size="sm" variant="ghost">
          Setup
        </Button>
      </Link>
      <Link
        to="/producer/projects/$projectId/script-book"
        params={{ projectId }}
        className="no-underline"
      >
        <Button size="sm" variant="ghost">
          Script book
        </Button>
      </Link>
      <Link
        to="/producer/projects/$projectId/characters"
        params={{ projectId }}
        className="no-underline"
      >
        <Button size="sm" variant="ghost">
          Characters
        </Button>
      </Link>
      <Link
        to="/producer/projects/$projectId/takes"
        params={{ projectId }}
        className="no-underline"
      >
        <Button size="sm" variant="ghost">
          Takes
        </Button>
      </Link>
      <Link
        to="/producer/projects/$projectId/financials"
        params={{ projectId }}
        className="no-underline"
      >
        <Button size="sm" variant="ghost">
          Financials
        </Button>
      </Link>
    </nav>
  )
}

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: string
  sub?: string
  tone?: 'green' | 'amber'
}) {
  const valueColor =
    tone === 'green'
      ? 'text-[var(--green)]'
      : tone === 'amber'
        ? 'text-[var(--amber)]'
        : 'text-[var(--text)]'
  return (
    <Card>
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{label}</div>
      <div className={`text-[22px] font-semibold tabular-nums leading-tight mt-0.5 ${valueColor}`}>
        {value}
      </div>
      {sub ? <div className="text-[12px] text-[var(--text-2)] mt-0.5">{sub}</div> : null}
    </Card>
  )
}

function CellRow({ label, cells }: { label: string; cells: BatchCell[] }) {
  return (
    <>
      <div className="truncate pr-2 text-[12px] text-[var(--text-2)]" title={label}>
        {label}
      </div>
      {cells.map((cell, i) => (
        <Tooltip
          key={i}
          content={`${label} · Batch ${cell.batch_index + 1}: ${cell.approved_lines}/${cell.total_lines} lines (${cell.state})`}
        >
          <div
            role="img"
            aria-label={`${label} batch ${cell.batch_index + 1} ${cell.state}`}
            className="h-5 w-5 rounded-[var(--radius-sm)]"
            style={{ background: STATE_COLOR[cell.state] }}
          />
        </Tooltip>
      ))}
    </>
  )
}

function AttentionCard({
  pendingTakes,
  unInvitedCharacters,
  projectId,
}: {
  pendingTakes: number
  unInvitedCharacters: number
  projectId: string
}) {
  const empty = pendingTakes === 0 && unInvitedCharacters === 0
  return (
    <Card title="Need my attention">
      {empty ? (
        <p className="m-0 text-[13px] text-[var(--text-2)]">All clear. Go grab a coffee.</p>
      ) : (
        <ul className="m-0 list-none space-y-2 p-0">
          {pendingTakes > 0 ? (
            <li className="flex items-center justify-between gap-2">
              <span className="text-[13px] text-[var(--text)]">
                {pendingTakes} take{pendingTakes === 1 ? '' : 's'} pending review
              </span>
              <Link to="/producer/projects/$projectId/takes" params={{ projectId }}>
                <Button size="sm" variant="ghost">
                  Open queue
                </Button>
              </Link>
            </li>
          ) : null}
          {unInvitedCharacters > 0 ? (
            <li className="flex items-center justify-between gap-2">
              <span className="text-[13px] text-[var(--text)]">
                {unInvitedCharacters} character{unInvitedCharacters === 1 ? '' : 's'} without an
                actor
              </span>
              <Link to="/producer/projects/$projectId/characters" params={{ projectId }}>
                <Button size="sm" variant="ghost">
                  Assign
                </Button>
              </Link>
            </li>
          ) : null}
        </ul>
      )}
    </Card>
  )
}

function RealtimePill() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-2)]">
      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[var(--green)]" />
      Live
    </span>
  )
}

function formatUsd(value: number | null | undefined): string {
  // Financials can be absent before the first contract lands — render an
  // honest em-dash instead of "$NaN" (Intl happily formats undefined to NaN).
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
  return `${Math.floor(ms / 86_400_000)}d ago`
}
